import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, FlexibleSelectDropdown, DropdownSort,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { MonetizationRequestStatus, MonetizationRequestType } from '@RedflagAI/node-utility/interface';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  initiateFetchMonetizationRequests,
  clearMonetizationRequests,
  fetchCompanyListRequest,
  clearCompanyListRequest,
} from '@Actions';
import { ProductFormDropdownSelect } from '@Components/Partial/Product/NewSearch';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
  SearchInput,
} from '../../../Partial/Product/SharedComponents';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { api: MonetizationRequestStatus.PENDING, display: 'Pending' },
  { api: MonetizationRequestStatus.UNDER_REVIEW, display: 'Under review' },
  { api: MonetizationRequestStatus.COMPLETED, display: 'Completed' },
];

const SORT_OPTIONS = [
  { api: 'created_at', display: 'Created at' },
  { api: 'request_status', display: 'Request status' },
];

const REQUEST_TYPE_LABELS = {
  [MonetizationRequestType.AUTOMATED]: 'Automated',
  [MonetizationRequestType.MANUAL]: 'Manual',
  [MonetizationRequestType.RELEASE]: 'Release',
};

const HEADER = [
  { display: 'Request ID', uid: 'requestId', size: '12%' },
  { display: 'Company', uid: 'companyId', size: '13%' },
  { display: 'Status', uid: 'requestStatus', size: '10%' },
  { display: 'Request Type', uid: 'requestType', size: '11%' },
  { display: 'Action Required', uid: 'actionRequired', size: '10%' },
  { display: 'Items', uid: 'items', size: '5%' },
  { display: 'Created', uid: 'createdAt', size: '14%' },
  { display: 'Updated', uid: 'updatedAt', size: '14%' },
  { display: '', uid: 'actions', size: '11%' },
];

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const AdminMonetizationRequestsListPage = ({
  small,
  location,
  fetchRequestsData,
  clearRequestsData,
  fetchCompanyList,
  clearCompanyList,
  loading,
  requestsData = null,
  companyList = null,
}) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;

  // Filters and pagination state
  const [currentPage, setCurrentPage] = React.useState(0);
  const [query, setQuery] = React.useState({});
  const [searchInput, setSearchInput] = React.useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const formattedCompanies = (companyList || []).map(({ id, name }) => ({
    api: id,
    display: name,
  }));

  // Build query params for API
  const queryParams = React.useMemo(() => {
    const p = {
      limit: PAGE_SIZE,
      skip: currentPage * PAGE_SIZE,
    };
    if (query.status) p.status = query.status;
    if (query.sortField) p.sortField = query.sortField;
    if (query.sortOrder) p.sortOrder = query.sortOrder;
    if (query.companyId) p.companyId = query.companyId;
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [currentPage, query, debouncedSearch]);

  // Reset page on filter change
  React.useEffect(() => {
    setCurrentPage(0);
  }, [query, debouncedSearch]);

  React.useEffect(() => {
    fetchRequestsData(queryParams);
    fetchCompanyList();
    return () => {
      clearRequestsData();
      clearCompanyList();
    };
  }, [queryParams, fetchRequestsData]);

  const handleQueryChange = (key, value) => {
    if (key === 'reset') {
      setQuery({});
      setSearchInput('');
      setCurrentPage(0);
    } else {
      setQuery((prev) => {
        const next = { ...prev, [key]: value };
        if (!value) delete next[key];
        return next;
      });
    }
  };

  const handleSortFieldChange = (field, direction) => {
    handleQueryChange('sortField', field);
    handleQueryChange('sortOrder', direction);
  };

  const handleSortDirectionToggle = () => {
    handleQueryChange('sortOrder', query.sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handlePage = (pageNum) => setCurrentPage(pageNum);

  // Table rendering
  const { data = [], pagination = {} } = requestsData || {};
  const { totalResults = 0, skip = 0, limit = PAGE_SIZE } = pagination;

  const renderCell = (uid, row) => {
    if (uid === 'items') {
      return (
        <Text size="1rem" color={page.tableText}>
          {row.items?.length || 0}
        </Text>
      );
    }

    if (uid === 'createdAt' || uid === 'updatedAt') {
      return (
        <Text size="1rem" color={page.tableText}>
          {buildDateTimeLabel(row[uid])}
        </Text>
      );
    }

    if (uid === 'companyId') {
      const company = formattedCompanies.find((c) => c.api === row.companyId.toString());
      return (
        <Text size="1rem" color={page.tableText}>
          {company ? `${company.display} (ID: ${company.api})` : row.companyId}
        </Text>
      );
    }

    if (uid === 'actionRequired') {
      let actionLabel = '—';
      let actionColor = page.secondaryText;

      if (row.requestType === MonetizationRequestType.RELEASE) {
        actionLabel = 'Release';
        actionColor = '#3b82f6';
      } else {
        const statuses = new Set((row.items || []).map((i) => i.requestedStatus));
        const hasMonetize = statuses.has('MONETIZATION_REQUESTED');
        const hasTakedown = statuses.has('TAKEDOWN_REQUESTED');
        if (hasMonetize && hasTakedown) {
          actionLabel = 'Mixed';
        } else if (hasTakedown) {
          actionLabel = 'Takedown';
          actionColor = '#e53e3e';
        } else if (hasMonetize) {
          actionLabel = 'Monetize';
          actionColor = '#00a855';
        }
      }

      return (
        <Box
          background={`${actionColor}22`}
          border={{ color: actionColor, size: '1px' }}
          round="6px"
          pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
          width="fit-content"
        >
          <Text size="0.8rem" weight={600} color={actionColor}>
            {actionLabel}
          </Text>
        </Box>
      );
    }

    if (uid === 'requestStatus') {
      let bg = page.inputBg;
      let color = page.primaryText;
      let border = page.inputBorder;

      if (row.requestStatus === MonetizationRequestStatus.COMPLETED) {
        bg = page.statusSuccess;
        color = '#fff';
        border = page.statusSuccess;
      } else if (row.requestStatus === MonetizationRequestStatus.UNDER_REVIEW) {
        bg = page.statusError;
        color = '#fff';
        border = page.statusError;
      }

      // Format label: "Completed", "Under Review", "Pending"
      const label = row.requestStatus
        ? String(row.requestStatus).replace(/_/g, ' ')
        : '—';

      return (
        <Box
          background={bg}
          border={{ color: border, size: '1px' }}
          round="6px"
          pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
          width="fit-content"
        >
          <Text size="0.8rem" weight={600} color={color}>
            {label}
          </Text>
        </Box>
      );
    }

    if (uid === 'actions') {
      return (
        <AppButton
          plain
          level="tableControls"
          label="View details"
          color={page.highlight}
          onClick={() => navigate(paths.adminMonetizationRequestDetails.replace(':id', row.requestId))}
        />
      );
    }

    if (uid === 'requestType') {
      return (
        <Text size="1rem" color={page.tableText}>
          {REQUEST_TYPE_LABELS[row[uid]] || '—'}
        </Text>
      );
    }

    return (
      <Text size={uid === 'requestId' ? '0.75rem' : '1rem'} color={page.tableText}>
        {row[uid] || '—'}
      </Text>
    );
  };

  const querySelected = query.status
    || query.sortField
    || query.sortOrder
    || query.companyId
    || searchInput;

  const renderContents = () => {
    if (loading || data?.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={data?.length === 0 && Boolean(querySelected)}
          refreshQuery={() => handleQueryChange('reset')}
          buttonHighlight={button.primaryBg}
          loading={loading}
          label="Loading monetization requests..."
          noDataLabel="Try adjusting your filters or check back later."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={size}
                pad={{
                  horizontal: '0.5rem',
                  top: '1rem',
                  bottom: '0.5rem',
                }}
              >
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {data.map((row) => (
          <Box
            key={row.id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{
                  horizontal: small ? '0' : '0.5rem',
                  vertical: small ? '0.25rem' : '0',
                }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderCell(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  // Table controls
  const tableControls = (
    <Box wrap direction="row" gap="0.5rem" align="center">
      <Box pad={{ vertical: small ? '0.5rem' : '0' }} width="12rem">
        <SearchInput
          key="search"
          customPlaceholder="Search by ID or company"
          small={small}
          handleSearch={(_, value) => handleQueryChange('search', value)}
          initialValue={searchInput}
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }} width="fit-content">
        <ProductFormDropdownSelect
          selectable
          searchable
          small={small}
          simpleOptions={null}
          handleFormValues={(val) => handleQueryChange('companyId', val)}
          label={(query.companyId
            ? `Company: ${formattedCompanies.find((c) => c.api === query.companyId)?.display || query.companyId}`
            : 'Filter by company'
          )}
          value={query.companyId || ''}
          autoSelect={false}
          inputConfig={{ overrideOptions: formattedCompanies }}
          customPad="0.25rem 0.3rem"
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => handleQueryChange('status', value || undefined)}
          flexibleOptions={STATUS_OPTIONS}
          noSelectionLabel="All statuses"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={STATUS_OPTIONS.find((o) => o.api === (query.status || ''))?.display || 'All statuses'}
          withReset={!!query.status}
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <DropdownSort
          sortOptions={SORT_OPTIONS}
          selectedSortField={query.sortField}
          sortDirection={query.sortOrder || 'desc'}
          onSortFieldChange={handleSortFieldChange}
          onSortDirectionToggle={handleSortDirectionToggle}
          textColor={page.inputText}
          iconColor={page.inputIcon}
          buttonBg={page.inputBg}
          customPad="0.2rem 0.3rem"
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <AppButton
          overrideHover
          disabled={!querySelected}
          onClick={() => handleQueryChange('reset')}
          level="tableControls"
          height="var(--table-controls-height)"
          color={button.primaryText}
          bgColor={button.primaryBg}
          label="Reset"
        />
      </Box>
    </Box>
  );

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Monetization Requests',
        subTitle: 'Content ID · Admin',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="Admin queue"
          text="View and manage all monetization requests submitted by customers. Use filters and sorting to find specific requests."
        />
        <Box width="100%">
          <TableWrapper
            zeroBasedPageIndex
            small={small}
            header="Monetization Requests"
            boxPad={tablePadding(small)}
            isPaged={totalResults > limit}
            totalResults={totalResults}
            currentResults={data.length}
            selectedResultsCount={limit}
            currentPage={currentPage}
            handlePage={handlePage}
            overrideNextPageDisabled={skip + limit >= totalResults}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    requestsData: state.adminMonetizationRequests.requests,
    companyList: state.adminCompany.companies,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchRequestsData: initiateFetchMonetizationRequests,
      clearRequestsData: clearMonetizationRequests,
      fetchCompanyList: fetchCompanyListRequest,
      clearCompanyList: clearCompanyListRequest,
    },
    dispatch,
  );
}

AdminMonetizationRequestsListPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  fetchRequestsData: PropTypes.func.isRequired,
  clearRequestsData: PropTypes.func.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  clearCompanyList: PropTypes.func.isRequired,
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  loading: PropTypes.bool.isRequired,
  requestsData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      requestId: PropTypes.string.isRequired,
      requestType: PropTypes.string.isRequired,
      companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      requestedByUserId: PropTypes.string,
      requestedByEmail: PropTypes.string,
      completedByUserId: PropTypes.string,
      completedByEmail: PropTypes.string,
      completedAt: PropTypes.string,
      requestStatus: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
      notes: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.shape({
        claimId: PropTypes.string.isRequired,
        requestedStatus: PropTypes.string.isRequired,
        previousStatus: PropTypes.string.isRequired,
        assetId: PropTypes.string.isRequired,
        videoTitle: PropTypes.string.isRequired,
        videoId: PropTypes.string.isRequired,
        videoUrl: PropTypes.string.isRequired,
        notes: PropTypes.string,
      })),
    })),
    pagination: PropTypes.shape({
      totalResults: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminMonetizationRequestsListPage);
